import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { Clock, Flame, RefreshCw, Heart } from "lucide-react";
import { Stars } from "@/components/Stars";

export function AlternativesDialog({ open, onClose, alternatives, loading, onChoose }) {
  const { t, tv } = useLang();
  const favCount = alternatives?.filter((a) => a.favorite).length || 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="alternatives-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{t("alt_title")}</DialogTitle>
          <DialogDescription>{t("alt_sub")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="h-40 grid place-items-center text-muted-foreground"><RefreshCw className="h-5 w-5 animate-spin" /></div>
        ) : !alternatives?.length ? (
          <p className="text-muted-foreground py-6 text-center">{t("no_alt")}</p>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {alternatives.map((a, i) => (
              <div key={a.recipe_id}>
                {i === 0 && a.favorite && <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5" data-testid="alt-favorites-heading">{t("my_favorites")}</p>}
                {i === favCount && favCount > 0 && <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-1.5 mt-2" data-testid="alt-suggestions-heading">{t("alt_suggestions")}</p>}
                <div className={`flex gap-3 rounded-2xl border p-3 items-center ${a.favorite ? "border-primary/60 bg-chenia-rosesoft/30" : "border-border"}`} data-testid={`alternative-${a.recipe_id}`}>
                  <img src={a.image} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" style={{ objectPosition: a.focal || "50% 50%" }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="font-semibold leading-snug truncate">{tv(a.name)}</p>
                      {a.favorite && <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-[10px] font-bold" data-testid={`alt-favorite-badge-${a.recipe_id}`}><Heart className="h-3 w-3 fill-current" /> {t("favorite_badge")}</span>}
                    </div>
                    {a.rating && <Stars value={a.rating} testid={`alt-rating-${a.recipe_id}`} />}
                    <p className="text-xs text-muted-foreground mt-0.5 inline-flex gap-3">
                      <span className="inline-flex items-center gap-1"><Flame className="h-3 w-3" /> {a.nutrition ? `${a.nutrition.calories} ${t("kcal")}` : "—"}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {a.total_time} {t("min")}</span>
                    </p>
                    {a.reasons?.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {a.reasons.map((r) => <span key={r} className="text-[11px] rounded-full bg-chenia-sage/30 px-2 py-0.5">{t(`r_${r}`)}</span>)}
                      </div>
                    )}
                  </div>
                  <Button size="sm" onClick={() => onChoose(a.recipe_id)} className="rounded-full bg-primary hover:bg-chenia-terracottadark shrink-0" data-testid={`choose-alternative-${a.recipe_id}`}>{t("choose_alt")}</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
